import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Globe, Loader2, Search, CheckSquare, Square, Save, Check, RefreshCcw } from 'lucide-react';
import { formatTokenCount, getItemExplanation } from './utils/formatters';
import { useRuntimeSettings } from './context/RuntimeSettingsContext';

const AnalyzeWebsitePage = () => {
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
  } = useRuntimeSettings();

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [loadingScan, setLoadingScan] = useState(false);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [scanError, setScanError] = useState('');
  const [analyzeError, setAnalyzeError] = useState('');
  const [scannedUrl, setScannedUrl] = useState('');
  const [scanMeta, setScanMeta] = useState(null);
  const [images, setImages] = useState([]);
  const [textSnippets, setTextSnippets] = useState([]);
  const [selectedTexts, setSelectedTexts] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [result, setResult] = useState(null);
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalNames, setOriginalNames] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);

  const allSelected = useMemo(
    () => images.length > 0 && selectedUrls.length === images.length,
    [images.length, selectedUrls.length],
  );

  const toggleSelected = (url) => {
    setSelectedUrls((prev) => (
      prev.includes(url) ? prev.filter((x) => x !== url) : [...prev, url]
    ));
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUrls([]);
    } else {
      setSelectedUrls(images.map((item) => item.url));
    }
  };

  const allTextsSelected = useMemo(
    () => textSnippets.length > 0 && selectedTexts.length === textSnippets.length,
    [textSnippets.length, selectedTexts.length],
  );

  const toggleSelectedText = (textValue) => {
    setSelectedTexts((prev) => (
      prev.includes(textValue) ? prev.filter((x) => x !== textValue) : [...prev, textValue]
    ));
  };

  const toggleSelectAllTexts = () => {
    if (allTextsSelected) {
      setSelectedTexts([]);
    } else {
      setSelectedTexts(textSnippets.map((item) => item.text));
    }
  };

  const handleScan = async () => {
    const url = String(websiteUrl || '').trim();
    if (!url) return;
    setLoadingScan(true);
    setScanError('');
    setAnalyzeError('');
    setResult(null);
    setScanMeta(null);
    setTextSnippets([]);
    setSelectedTexts([]);

    try {
      const response = await axios.post('/scan_website_images', {
        url,
        model: modelName,
      });
      const nextImages = Array.isArray(response?.data?.images) ? response.data.images : [];
      setImages(nextImages);
      setSelectedUrls(
        nextImages
          .filter((item) => item?.selected !== false)
          .map((item) => item.url),
      );
      const nextTextSnippets = Array.isArray(response?.data?.text_snippets) ? response.data.text_snippets : [];
      setTextSnippets(nextTextSnippets);
      setSelectedTexts(
        nextTextSnippets
          .filter((item) => item?.selected !== false)
          .map((item) => item.text),
      );
      setScannedUrl(response?.data?.url || url);
      setScanMeta({
        selectedCount: Number(response?.data?.selected_count || 0),
        duplicateCount: Number(response?.data?.duplicate_count || 0),
        relevanceAppliedCount: Number(response?.data?.relevance_applied_count || 0),
        relevanceModel: response?.data?.relevance_model || modelName,
        selectedTextCount: Number(response?.data?.selected_text_count || 0),
        textCount: Number(response?.data?.text_count || 0),
      });
    } catch (error) {
      setScanError(error?.response?.data?.message || error?.message || 'Failed to scan website.');
      setImages([]);
      setSelectedUrls([]);
      setTextSnippets([]);
      setSelectedTexts([]);
      setScannedUrl('');
      setScanMeta(null);
    } finally {
      setLoadingScan(false);
    }
  };

  const handleAnalyze = async () => {
    if (selectedUrls.length === 0) return;
    setLoadingAnalyze(true);
    setAnalyzeError('');
    setResult(null);
    try {
      const response = await axios.post('/analyze', {
        image_urls: selectedUrls,
        website_context_text: selectedTexts,
        model: modelName,
        resize_mode: resizeMode,
        compression_mode: compressionMode,
        use_ebay_rag: useEbayRag,
        use_notes_rag: useNotesRag,
      });
      const resultData = response?.data || {};
      resultData.items = (Array.isArray(resultData.items) ? resultData.items : []).map((item) => {
        if (!item || typeof item !== 'object') return item;
        const explanation = getItemExplanation(item);
        return {
          ...item,
          explanation,
          margin_analysis: explanation,
        };
      });
      setResult(resultData);

      const prices = {};
      const names = {};
      (resultData.items || []).forEach((item) => {
        if (item?.id) {
          prices[item.id] = item.actual_price;
          names[item.id] = item.manual_name || '';
        }
      });
      setOriginalPrices(prices);
      setOriginalNames(names);
    } catch (error) {
      setAnalyzeError(error?.response?.data?.message || error?.message || 'Analyze failed.');
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const handlePriceChange = (id, value) => {
    setResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === id ? { ...item, actual_price: value } : item)),
      };
    });
  };

  const handleManualNameChange = (id, value) => {
    setResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === id ? { ...item, manual_name: value } : item)),
      };
    });
  };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      await axios.post('/update_price', { id, actual_price: parseFloat(price) });
      setOriginalPrices((prev) => ({ ...prev, [id]: parseFloat(price) }));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to save price.');
    } finally {
      setUpdatingId(null);
    }
  };

  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      await axios.post('/update_item_name', { id, item_name: name });
      setOriginalNames((prev) => ({ ...prev, [id]: name }));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to save item name.');
    } finally {
      setUpdatingNameId(null);
    }
  };

  const usage = (result && typeof result.usage === 'object') ? result.usage : {};
  const inputTokens = usage.prompt_tokens ?? usage.input_tokens ?? null;
  const outputTokens = usage.completion_tokens ?? usage.output_tokens ?? null;
  const totalTokens = usage.total_tokens ?? (
    Number.isFinite(Number(inputTokens)) && Number.isFinite(Number(outputTokens))
      ? (Number(inputTokens) + Number(outputTokens))
      : null
  );
  const items = Array.isArray(result?.items) ? result.items : [];
  const cacheHit = (
    result?.cache_hit === true ||
    String(result?.analysis_source || '').toLowerCase() === 'cache'
  );
  const analysisSourceLabel = cacheHit ? 'CACHE' : 'LIVE';
  const analysisSourceClass = cacheHit
    ? 'bg-orange-100 text-orange-700 border border-orange-200'
    : 'bg-green-100 text-green-700 border border-green-200';
  const resultUseEbayRag = (
    result?.use_ebay_rag !== undefined
      ? Boolean(result.use_ebay_rag)
      : useEbayRag
  );
  const ragRefs = Math.max(0, Number(result?.ebay_rag_result_count || 0) || 0);

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Analyse web-site</h1>

      <div className="mb-4 bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200 space-y-3">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2">
          <span className="text-sm font-medium text-slate-700 shrink-0">URL:</span>
          <div className="relative flex-1">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com/listing"
              className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <button
            type="button"
            onClick={handleScan}
            disabled={loadingScan || !websiteUrl.trim()}
            className={`px-3 py-1.5 rounded text-sm font-medium text-white ${loadingScan || !websiteUrl.trim() ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            title="Scan website"
          >
            {loadingScan
              ? <span className="inline-flex items-center gap-1"><Loader2 size={14} className="animate-spin" />Scanning</span>
              : <span className="inline-flex items-center gap-1"><Search size={14} />Scan</span>
            }
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 text-sm">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Model:</span>
            <select value={modelName} onChange={(e) => setModelName(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
              <optgroup label="GPT-5 Series (Latest)">
                <option value="gpt-5.4">gpt-5.4</option>
                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                <option value="gpt-5.2">gpt-5.2</option>
                <option value="gpt-5.1">gpt-5.1</option>
                <option value="gpt-5">gpt-5</option>
              </optgroup>
              <optgroup label="Gemini">
                <option value="gemini-flash-latest">gemini-flash-latest</option>
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
              </optgroup>
              <optgroup label="Claude">
                <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                <option value="claude-haiku-4-5">claude-haiku-4-5</option>
                <option value="claude-opus-4-6">claude-opus-4-6</option>
                <option value="claude-opus-4-7">claude-opus-4-7</option>
              </optgroup>
            </select>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Resize:</span>
            <select value={resizeMode} onChange={(e) => setResizeMode(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
              <option value="original">Original</option>
              <option value="1600">Max 1600px</option>
              <option value="1280">Max 1280px</option>
              <option value="1024">Max 1024px</option>
              <option value="768">Max 768px</option>
              <option value="512">Max 512px</option>
              <option value="128">Max 128px</option>
            </select>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Compression:</span>
            <select value={compressionMode} onChange={(e) => setCompressionMode(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={useEbayRag} onChange={(e) => setUseEbayRag(e.target.checked)} />
            <span>eBay RAG</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={useNotesRag} onChange={(e) => setUseNotesRag(e.target.checked)} />
            <span>Notes RAG</span>
          </label>
        </div>
      </div>

      {scanError ? <div className="mb-3 text-sm text-red-600">{scanError}</div> : null}
      {scannedUrl ? <div className="mb-3 text-xs text-slate-500 break-all">Scanned: {scannedUrl}</div> : null}
      {scanMeta ? (
        <div className="mb-3 text-xs text-slate-600">
          Auto-selected {scanMeta.selectedCount}/{images.length} | Duplicates: {scanMeta.duplicateCount} | LLM relevance checked: {scanMeta.relevanceAppliedCount} ({scanMeta.relevanceModel})
          {` | Text selected: ${scanMeta.selectedTextCount}/${scanMeta.textCount}`}
        </div>
      ) : null}

      <div className="mb-4 border border-slate-200 rounded-lg">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Detected Images ({images.length})</div>
          <button type="button" onClick={toggleSelectAll} disabled={images.length === 0} className="text-sm text-blue-600 disabled:text-slate-400 inline-flex items-center gap-1">
            {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="max-h-[420px] overflow-auto p-2 space-y-2">
          {images.length === 0 ? (
            <div className="text-sm text-slate-500 p-2">No images yet. Enter URL and scan.</div>
          ) : images.map((img, index) => (
            <label key={`${img.url}-${index}`} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedUrls.includes(img.url)}
                onChange={() => toggleSelected(img.url)}
              />
              <img src={img.url} alt={img.alt || `Image ${index + 1}`} className="w-14 h-14 object-cover rounded border border-slate-200 bg-slate-100" />
              <div className="min-w-0">
                <div className="text-sm text-slate-700 truncate">{img.alt || `Image ${index + 1}`}</div>
                <div className="text-xs text-slate-500 truncate">{img.url}</div>
                <div className={`text-[11px] ${img.selected !== false ? 'text-emerald-600' : 'text-amber-700'} truncate`}>
                  {img.selection_reason || (img.selected !== false ? 'selected' : 'deselected')}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4 border border-slate-200 rounded-lg">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Relevant Text Snippets ({textSnippets.length})</div>
          <button type="button" onClick={toggleSelectAllTexts} disabled={textSnippets.length === 0} className="text-sm text-blue-600 disabled:text-slate-400 inline-flex items-center gap-1">
            {allTextsSelected ? <CheckSquare size={14} /> : <Square size={14} />}
            {allTextsSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="max-h-[260px] overflow-auto p-2 space-y-2">
          {textSnippets.length === 0 ? (
            <div className="text-sm text-slate-500 p-2">No text snippets extracted.</div>
          ) : textSnippets.map((row, index) => (
            <label key={`${row.text}-${index}`} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTexts.includes(row.text)}
                onChange={() => toggleSelectedText(row.text)}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="text-sm text-slate-700">{row.text}</div>
                <div className={`text-[11px] ${row.selected !== false ? 'text-emerald-600' : 'text-amber-700'} truncate`}>
                  {row.selection_reason || (row.selected !== false ? 'selected' : 'deselected')}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handleAnalyze}
        disabled={loadingAnalyze || selectedUrls.length === 0}
        className={`w-full py-3 rounded-lg text-white font-semibold ${loadingAnalyze || selectedUrls.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
      >
        {loadingAnalyze ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Analyzing...</span> : `Analyze selected (${selectedUrls.length})`}
      </button>

      {analyzeError ? <div className="mt-3 text-sm text-red-600">{analyzeError}</div> : null}

      {result ? (
        <div className="mt-6 border border-slate-200 rounded-lg p-4 bg-slate-50">
          <div className="flex flex-wrap gap-2 text-xs mb-3">
            <span className="text-[10px] font-mono text-slate-500 bg-white border px-2 py-0.5 rounded">
              {result.model || modelName}
            </span>
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
              Resize: {result.resize_mode || resizeMode}
            </span>
            <span className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">
              Compression: {result.compression_mode || compressionMode}
            </span>
            <span className="text-[10px] font-mono text-cyan-700 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded">
              eBay RAG: {resultUseEbayRag ? `on (${ragRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
              {result.analysis_duration_ms !== undefined && result.analysis_duration_ms !== null
                ? `${Number(result.analysis_duration_ms).toFixed(0)}ms`
                : 'N/A'}
            </span>
            <span className="text-[10px] font-mono text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">
              {result.analysis_cost_display !== undefined
                ? result.analysis_cost_display
                : (result.analysis_cost !== undefined && result.analysis_cost !== null
                  ? `$${Number(result.analysis_cost).toFixed(4)}`
                  : 'N/A')}
            </span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${analysisSourceClass}`}>
              Src: {analysisSourceLabel}
            </span>
            <span className="text-[10px] font-mono text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
              In Tok: {formatTokenCount(inputTokens)}
            </span>
            <span className="text-[10px] font-mono text-violet-700 bg-violet-100 border border-violet-200 px-2 py-0.5 rounded">
              Out Tok: {formatTokenCount(outputTokens)}
            </span>
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
              Tot Tok: {formatTokenCount(totalTokens)}
            </span>
            <span className="text-[10px] font-mono text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
              Items: {items.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((item, index) => {
              const isDirty = item.id && originalPrices[item.id] !== undefined && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
              const isUpdating = updatingId === item.id;
              const isNameDirty = item.id && originalNames[item.id] !== undefined && originalNames[item.id] !== (item.manual_name || '');
              const isNameUpdating = updatingNameId === item.id;
              const condition = item?.condition || 'Unknown';
              const priceSource = item?.price_source || 'model_knowledge';
              const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
              const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
              const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';

              return (
                <div key={`${item.item || 'item'}-${index}`} className="bg-white rounded border border-slate-200 p-3 shadow-sm">
                  <strong className="block text-slate-700 text-base mb-1">{item.item || `Item ${index + 1}`}</strong>

                  <div className="flex flex-col gap-1 mb-2">
                    <span className="text-slate-500 text-xs">Manual Name:</span>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        placeholder="Enter custom name..."
                        className={`w-full pr-8 py-1 px-2 border rounded text-sm text-slate-700 focus:outline-none focus:ring-1 transition-all ${
                          isNameDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                        }`}
                        value={item.manual_name || ''}
                        onChange={(e) => handleManualNameChange(item.id, e.target.value)}
                      />
                      {item.id && (
                        <button
                          onClick={() => updateItemName(item.id, item.manual_name || '')}
                          disabled={!isNameDirty || isNameUpdating}
                          className={`absolute right-1 p-1 rounded transition-all ${
                            isNameUpdating ? 'text-slate-400' : isNameDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default'
                          }`}
                        >
                          {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isNameDirty ? <Save size={14} /> : <Check size={14} />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-slate-600">
                      Estimated Value: <span className="font-bold text-green-600">{Number.isFinite(Number(item.estimated_value)) ? `$${Number(item.estimated_value).toFixed(2)}` : 'n/a'}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Actual Price:</span>
                      <div className="relative flex items-center">
                        <span className="absolute left-2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={item.actual_price}
                          onChange={(e) => handlePriceChange(item.id, e.target.value)}
                          className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                            isDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                          }`}
                          step="0.01"
                        />
                        {item.id && (
                          <button
                            onClick={() => updatePrice(item.id, item.actual_price)}
                            disabled={!isDirty || isUpdating}
                            className={`absolute right-1 p-1 rounded transition-all ${
                              isUpdating ? 'text-slate-400' : isDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default'
                            }`}
                          >
                            {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isDirty ? <Save size={14} /> : <Check size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[9px] text-slate-400">Condition:</span>
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
                    <span className="text-[9px] text-slate-400 ml-1">AI Src:</span>
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
                  </div>

                  {item.explanation && (
                    <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 leading-relaxed">
                      {item.explanation}
                    </div>
                  )}
                </div>
              );
            })}
            {items.length === 0 ? <div className="text-sm text-slate-500">No items returned.</div> : null}
          </div>
          {items.length >= 1 && (
            <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg flex justify-between items-center">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
              <span className="text-base text-green-600 font-bold">
                ${items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AnalyzeWebsitePage;
